import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Menu, MenuItem, ListItemIcon, ListItemText, Chip, Tooltip, LinearProgress,
  Divider, Checkbox, Paper, CircularProgress, InputAdornment, Avatar, Snackbar, Alert,
  Breadcrumbs, Link as MLink,
} from '@mui/material';
import {
  Folder as FolderIcon, FolderShared as FolderSharedIcon, InsertDriveFile as FileIcon,
  Image as ImageIcon, PictureAsPdf as PdfIcon, AudioFile as AudioIcon, VideoFile as VideoIcon,
  Archive as ArchiveIcon, Description as DocIcon, TableChart as SheetIcon, Code as CodeIcon,
  CreateNewFolder as NewFolderIcon, CloudUpload as UploadIcon, Search as SearchIcon,
  Star as StarIcon, StarBorder as StarBorderIcon, Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon, RestoreFromTrash as RestoreIcon,
  MoreVert as MoreVertIcon, DriveFileRenameOutline as RenameIcon,
  DriveFileMove as MoveIcon, ContentCopy as CopyIcon, FileCopy as DuplicateIcon,
  Share as ShareIcon, Link as LinkIcon, Download as DownloadIcon, Visibility as PreviewIcon,
  GridView as GridViewIcon, ViewList as ListViewIcon, Home as HomeIcon,
  ChevronRight as ChevronRightIcon, ArrowUpward as ArrowUpIcon,
  Add as AddIcon, ArrowDropDown as ArrowDropDownIcon, Close as CloseIcon,
  Sort as SortIcon, Info as InfoIcon, Computer as ComputerIcon, People as PeopleIcon,
  Schedule as ClockIcon, OpenInNew as OpenInNewIcon, Cloud as CloudIcon,
  History as HistoryIcon, Backup as BackupIcon, Tune as TuneIcon,
  CheckCircle as CheckCircleIcon, MusicNote as MusicNoteIcon,
} from '@mui/icons-material';
import api, { extractData, handleApiError } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

// ─────────────────────────── Types ───────────────────────────
interface DriveItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  mimeType?: string;
  fileSize?: number;
  ownerId: string;
  ownerEmail?: string;
  parentId?: string | null;
  sharedWith: Array<{ userId: string; email: string; permission: string; sharedAt: string }>;
  shareLink?: string;
  isShareLinkEnabled?: boolean;
  shareLinkPermission?: string;
  isStarred: boolean;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface StorageInfo {
  totalFiles: number;
  totalFolders: number;
  totalSize: number;
  quotaBytes: number;
  quotaUsedBytes: number;
  byType?: Record<string, number>;
}

interface BreadcrumbNode { id: string; name: string; }

type ViewTab = 'files' | 'recent' | 'shared' | 'starred' | 'trash';
type CategoryFilter = 'all' | 'image' | 'pdf' | 'video' | 'audio' | 'archive' | 'doc';
type SortKey = 'updatedAt' | 'name' | 'fileSize';

// ─────────────────────────── Helpers ─────────────────────────
const formatBytes = (b?: number) => {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(1)} GB`;
};
const formatRel = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} d ago`;
  return d.toLocaleDateString();
};

const mimeCategory = (m?: string): CategoryFilter => {
  if (!m) return 'all';
  if (m.startsWith('image/')) return 'image';
  if (m === 'application/pdf') return 'pdf';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  if (/zip|tar|rar|7z|gzip/.test(m)) return 'archive';
  if (/wordprocessing|spreadsheet|document|presentation|msword|excel|powerpoint|text\//.test(m)) return 'doc';
  return 'all';
};

const itemIcon = (it: DriveItem, size = 22) => {
  if (it.type === 'folder') {
    const shared = it.sharedWith && it.sharedWith.length > 0;
    return shared
      ? <FolderSharedIcon sx={{ fontSize: size, color: '#1a73e8' }} />
      : <FolderIcon sx={{ fontSize: size, color: '#5f6368' }} />;
  }
  const m = it.mimeType || '';
  if (m.startsWith('image/'))                return <ImageIcon sx={{ fontSize: size, color: '#1a73e8' }} />;
  if (m === 'application/pdf')               return <PdfIcon sx={{ fontSize: size, color: '#ea4335' }} />;
  if (m.startsWith('video/'))                return <VideoIcon sx={{ fontSize: size, color: '#0f9d58' }} />;
  if (m.startsWith('audio/'))                return <AudioIcon sx={{ fontSize: size, color: '#f9ab00' }} />;
  if (/zip|tar|rar|7z|gzip/.test(m))         return <ArchiveIcon sx={{ fontSize: size, color: '#5f6368' }} />;
  if (/spreadsheet|excel/.test(m))           return <SheetIcon sx={{ fontSize: size, color: '#0f9d58' }} />;
  if (/wordprocessing|msword|document/.test(m)) return <DocIcon sx={{ fontSize: size, color: '#1a73e8' }} />;
  if (/text\/|json|xml|javascript|html|css/.test(m)) return <CodeIcon sx={{ fontSize: size, color: '#5f6368' }} />;
  return <FileIcon sx={{ fontSize: size, color: '#5f6368' }} />;
};

const isPreviewable = (m?: string) => {
  if (!m) return false;
  return m.startsWith('image/') || m === 'application/pdf' || m.startsWith('video/') || m.startsWith('audio/') || m.startsWith('text/');
};

// ─────────────────────────── Component ───────────────────────
export default function DrivexPage() {
  const { user } = useAuthStore();
  const myId = (user as any)?.id;
  const myEmail = (user as any)?.email;

  // State
  const [tab, setTab] = useState<ViewTab>('files');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbNode[]>([]);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [storage, setStorage] = useState<StorageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'grid' | 'list'>('list');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
  const [sortAsc, setSortAsc] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ name: string; pct: number } | null>(null);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  // Menus & dialogs
  const [newMenu, setNewMenu] = useState<HTMLElement | null>(null);
  const [sortMenu, setSortMenu] = useState<HTMLElement | null>(null);
  const [ctx, setCtx] = useState<{ el: HTMLElement; item: DriveItem } | null>(null);
  const [filterMenu, setFilterMenu] = useState<{ el: HTMLElement; type: 'type' | 'people' | 'modified' } | null>(null);
  const [createDlg, setCreateDlg] = useState<{ open: boolean; name: string }>({ open: false, name: '' });
  const [renameDlg, setRenameDlg] = useState<{ open: boolean; item: DriveItem | null; name: string }>({ open: false, item: null, name: '' });
  const [shareDlg, setShareDlg] = useState<{ open: boolean; item: DriveItem | null }>({ open: false, item: null });
  const [shareEmail, setShareEmail] = useState('');
  const [sharePerm, setSharePerm] = useState<'view' | 'edit'>('view');
  const [previewDlg, setPreviewDlg] = useState<{ open: boolean; item: DriveItem | null }>({ open: false, item: null });
  const [showInfo, setShowInfo] = useState(false);

  const fileInput = useRef<HTMLInputElement | null>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const dragCounter = useRef(0);

  // ─────────── Data fetch ───────────
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('tab', tab);
      if (currentFolder && tab === 'files') params.set('parentId', currentFolder);
      if (search.trim()) params.set('search', search.trim());
      params.set('sortBy', sortKey);
      params.set('sortOrder', sortAsc ? 'asc' : 'desc');
      const res = await api.get(`/drivex?${params.toString()}`);
      setItems(extractData<DriveItem[]>(res));
    } catch (err) {
      setSnack({ msg: handleApiError(err), sev: 'error' });
    } finally { setLoading(false); }
  }, [tab, currentFolder, search, sortKey, sortAsc]);

  const fetchBreadcrumb = useCallback(async () => {
    if (!currentFolder || tab !== 'files') { setBreadcrumb([]); return; }
    try {
      const res = await api.get(`/drivex/${currentFolder}/breadcrumb`);
      setBreadcrumb(extractData<BreadcrumbNode[]>(res) || []);
    } catch { setBreadcrumb([]); }
  }, [currentFolder, tab]);

  const fetchStorage = useCallback(async () => {
    try {
      const res = await api.get('/drivex/storage');
      setStorage(extractData<StorageInfo>(res));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchBreadcrumb(); }, [fetchBreadcrumb]);
  useEffect(() => { fetchStorage(); }, [fetchStorage]);

  // Keyboard shortcuts
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      if (e.key === '/' && !inField) { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === 'Escape' && selected.size > 0) { setSelected(new Set()); return; }
      if (e.key === 'Delete' && selected.size > 0 && !inField) { e.preventDefault(); bulkTrash(); }
      if ((e.key === 'a' || e.key === 'A') && (e.ctrlKey || e.metaKey) && !inField) {
        e.preventDefault(); setSelected(new Set(filtered.map(i => i.id)));
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, items, category]);

  // ─────────── Filter/sort ───────────
  const filtered = useMemo(() => {
    let list = items.slice();
    if (category !== 'all') list = list.filter(i => i.type === 'folder' || mimeCategory(i.mimeType) === category);
    return list;
  }, [items, category]);

  const folders = useMemo(() => filtered.filter(i => i.type === 'folder'), [filtered]);
  const files = useMemo(() => filtered.filter(i => i.type === 'file'), [filtered]);

  const suggested = useMemo(() => {
    return items.filter(i => i.type === 'file' && !i.isDeleted)
      .slice().sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt))
      .slice(0, 4);
  }, [items]);

  // ─────────── Actions ───────────
  const navigateTo = (folderId: string | null) => {
    setCurrentFolder(folderId);
    setSelected(new Set());
    setCategory('all');
  };
  const goUp = () => {
    if (breadcrumb.length === 0) return;
    if (breadcrumb.length === 1) navigateTo(null);
    else navigateTo(breadcrumb[breadcrumb.length - 2].id);
  };

  const toggleSelect = (id: string, additive = false) => {
    setSelected(prev => {
      const next = additive ? new Set(prev) : new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openItem = (it: DriveItem) => {
    if (it.isDeleted) return;
    if (it.type === 'folder') navigateTo(it.id);
    else if (isPreviewable(it.mimeType)) setPreviewDlg({ open: true, item: it });
    else handleDownload(it);
  };

  const handleDownload = async (it: DriveItem) => {
    try {
      const res = await api.get(`/drivex/${it.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.download = it.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const uploadFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    for (const f of arr) {
      const fd = new FormData();
      fd.append('file', f);
      if (currentFolder) fd.append('parentId', currentFolder);
      setUploadProgress({ name: f.name, pct: 0 });
      try {
        await api.post('/drivex/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e: any) => {
            if (e.total) setUploadProgress({ name: f.name, pct: Math.round((e.loaded / e.total) * 100) });
          },
        });
      } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
    }
    setUploadProgress(null);
    setSnack({ msg: `Uploaded ${arr.length} file${arr.length > 1 ? 's' : ''}`, sev: 'success' });
    fetchItems(); fetchStorage();
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) { dragCounter.current++; setDragOver(true); }
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setDragOver(false); }
  };
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); dragCounter.current = 0; setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) uploadFiles(e.dataTransfer.files);
  };

  const createFolder = async () => {
    if (!createDlg.name.trim()) return;
    try {
      await api.post('/drivex/folder', { name: createDlg.name.trim(), parentId: currentFolder });
      setCreateDlg({ open: false, name: '' });
      fetchItems();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const renameItem = async () => {
    if (!renameDlg.item || !renameDlg.name.trim()) return;
    try {
      await api.put(`/drivex/${renameDlg.item.id}/rename`, { name: renameDlg.name.trim() });
      setRenameDlg({ open: false, item: null, name: '' });
      fetchItems();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const toggleStar = async (it: DriveItem) => {
    try { await api.put(`/drivex/${it.id}/star`); fetchItems(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const trashItem = async (it: DriveItem) => {
    try { await api.delete(`/drivex/${it.id}`); fetchItems(); fetchStorage(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const restoreItem = async (it: DriveItem) => {
    try { await api.post(`/drivex/${it.id}/restore`); fetchItems(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const permDelete = async (it: DriveItem) => {
    if (!confirm(`Permanently delete "${it.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/drivex/${it.id}/permanent`); fetchItems(); fetchStorage(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const duplicate = async (it: DriveItem) => {
    try { await api.post(`/drivex/${it.id}/copy`); fetchItems(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const bulkTrash = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await api.post('/drivex/batch/delete', { ids });
      setSelected(new Set());
      setSnack({ msg: `Moved ${ids.length} item${ids.length > 1 ? 's' : ''} to trash`, sev: 'success' });
      fetchItems(); fetchStorage();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const bulkStar = async (star: boolean) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await api.post('/drivex/batch/star', { ids, star });
      fetchItems();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const emptyTrash = async () => {
    if (!confirm('Empty trash permanently? This cannot be undone.')) return;
    try { await api.delete('/drivex/trash/empty'); fetchItems(); fetchStorage(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const addShare = async () => {
    if (!shareDlg.item || !shareEmail) return;
    try {
      await api.post(`/drivex/${shareDlg.item.id}/share`, { email: shareEmail, permission: sharePerm });
      setShareEmail('');
      const res = await api.get(`/drivex?parentId=${shareDlg.item.parentId || ''}`);
      const updated = (extractData<DriveItem[]>(res)).find(x => x.id === shareDlg.item?.id);
      if (updated) setShareDlg({ open: true, item: updated });
      fetchItems();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const removeShare = async (email: string) => {
    if (!shareDlg.item) return;
    try {
      await api.delete(`/drivex/${shareDlg.item.id}/share/${encodeURIComponent(email)}`);
      const res = await api.get(`/drivex?parentId=${shareDlg.item.parentId || ''}`);
      const updated = (extractData<DriveItem[]>(res)).find(x => x.id === shareDlg.item?.id);
      if (updated) setShareDlg({ open: true, item: updated });
      fetchItems();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const toggleShareLink = async (it: DriveItem) => {
    try {
      await api.post(`/drivex/${it.id}/share-link`, { enabled: !it.isShareLinkEnabled, permission: 'view' });
      fetchItems();
      const fresh = items.find(x => x.id === it.id);
      if (shareDlg.open && fresh) setShareDlg({ open: true, item: fresh });
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const copyShareLink = (it: DriveItem) => {
    if (it.shareLink) {
      navigator.clipboard.writeText(`${window.location.origin}/drivex/shared/${it.shareLink}`);
      setSnack({ msg: 'Share link copied', sev: 'success' });
    }
  };

  // Derived counts for sidebar
  const counts = useMemo(() => ({
    starred: items.filter(i => i.isStarred && !i.isDeleted).length,
    shared: items.filter(i => i.ownerId !== myId && !i.isDeleted).length,
    trash: items.filter(i => i.isDeleted).length,
  }), [items, myId]);

  // ─────────────────────────── Render ───────────────────────────
  return (
    <Box
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      sx={{ display: 'flex', height: '100%', bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}
    >
      {/* ─── Internal DriveX rail ─── */}
      <Box sx={{ width: 256, borderRight: '1px solid #e8eaed', display: 'flex', flexDirection: 'column', flexShrink: 0, py: 2, gap: 0.5, overflowY: 'auto' }}>
        <Box sx={{ px: 2, mb: 1.5 }}>
          <Button
            onClick={(e) => setNewMenu(e.currentTarget)}
            startIcon={<Box sx={{ width: 22, height: 22, borderRadius: '50%', background: 'conic-gradient(#1a73e8, #34a853, #fbbc04, #ea4335, #1a73e8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><AddIcon sx={{ color: '#fff', fontSize: 16 }} /></Box>}
            sx={{ bgcolor: '#fff', color: '#202124', border: '1px solid #dadce0', borderRadius: '24px', textTransform: 'none', fontSize: '0.9rem', fontWeight: 500, px: 2.5, py: 1, boxShadow: '0 1px 3px rgba(60,64,67,.15)', '&:hover': { bgcolor: '#f8faff', boxShadow: '0 1px 3px rgba(60,64,67,.3)' } }}
          >
            New
          </Button>
        </Box>

        <RailItem icon={<FolderIcon />} label="My DriveX" active={tab === 'files'} onClick={() => { setTab('files'); navigateTo(null); }} />
        <RailItem icon={<ComputerIcon />} label="Computers" />
        <RailItem icon={<PeopleIcon />} label="Shared with me" active={tab === 'shared'} onClick={() => { setTab('shared'); navigateTo(null); }} count={counts.shared || undefined} />
        <RailItem icon={<ClockIcon />} label="Recent" active={tab === 'recent'} onClick={() => { setTab('recent'); navigateTo(null); }} />
        <RailItem icon={<StarIcon />} label="Starred" active={tab === 'starred'} onClick={() => { setTab('starred'); navigateTo(null); }} count={counts.starred || undefined} />
        <RailItem icon={<DeleteIcon />} label="Trash" active={tab === 'trash'} onClick={() => { setTab('trash'); navigateTo(null); }} count={counts.trash || undefined} />

        <Divider sx={{ mx: 2, my: 1 }} />
        <Typography sx={{ px: 2, fontSize: '0.7rem', fontWeight: 600, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Categories</Typography>
        <RailItem icon={<ImageIcon sx={{ color: '#1a73e8' }} />} label="Photos & images" active={category === 'image'} onClick={() => setCategory(category === 'image' ? 'all' : 'image')} />
        <RailItem icon={<PdfIcon sx={{ color: '#ea4335' }} />} label="PDFs" active={category === 'pdf'} onClick={() => setCategory(category === 'pdf' ? 'all' : 'pdf')} />
        <RailItem icon={<VideoIcon sx={{ color: '#0f9d58' }} />} label="Videos" active={category === 'video'} onClick={() => setCategory(category === 'video' ? 'all' : 'video')} />
        <RailItem icon={<MusicNoteIcon sx={{ color: '#f9ab00' }} />} label="Audio" active={category === 'audio'} onClick={() => setCategory(category === 'audio' ? 'all' : 'audio')} />
        <RailItem icon={<ArchiveIcon sx={{ color: '#5f6368' }} />} label="Archives" active={category === 'archive'} onClick={() => setCategory(category === 'archive' ? 'all' : 'archive')} />

        <Divider sx={{ mx: 2, my: 1 }} />
        <Typography sx={{ px: 2, fontSize: '0.7rem', fontWeight: 600, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5 }}>Quick access</Typography>
        <RailItem icon={<HistoryIcon />} label="Activity" />
        <RailItem icon={<BackupIcon />} label="Backups" />

        {/* Storage */}
        {storage && (
          <Box sx={{ mx: 1.5, mt: 2, p: 1.5, border: '1px solid #e8eaed', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CloudIcon sx={{ fontSize: 14, color: '#5f6368' }} />
                <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>Storage</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.7rem', color: '#80868b' }}>
                {formatBytes(storage.quotaUsedBytes)} / {formatBytes(storage.quotaBytes)}
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={Math.min(100, storage.quotaBytes ? (storage.quotaUsedBytes / storage.quotaBytes) * 100 : 0)} sx={{ height: 4, borderRadius: 2, bgcolor: '#e8eaed', '& .MuiLinearProgress-bar': { bgcolor: '#1a73e8' } }} />
          </Box>
        )}
      </Box>

      {/* ─── Main pane ─── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
        {/* Toolbar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.5, borderBottom: '1px solid #f1f3f4' }}>
          <Breadcrumbs separator={<ChevronRightIcon sx={{ fontSize: 18, color: '#5f6368' }} />} sx={{ '& .MuiBreadcrumbs-li': { display: 'flex' } }}>
            <Box onClick={() => navigateTo(null)} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', px: 1, py: 0.5, borderRadius: 1, fontSize: '1.05rem', fontWeight: 500, color: '#202124', '&:hover': { bgcolor: '#f1f3f4' } }}>
              <HomeIcon sx={{ fontSize: 18 }} />
              {tab === 'files' && 'My DriveX'}
              {tab === 'recent' && 'Recent'}
              {tab === 'shared' && 'Shared with me'}
              {tab === 'starred' && 'Starred'}
              {tab === 'trash' && 'Trash'}
            </Box>
            {breadcrumb.map((n, i) => (
              <Box key={n.id} onClick={() => i === breadcrumb.length - 1 ? null : navigateTo(n.id)} sx={{ cursor: i === breadcrumb.length - 1 ? 'default' : 'pointer', px: 1, py: 0.5, borderRadius: 1, fontSize: '1rem', color: i === breadcrumb.length - 1 ? '#5f6368' : '#202124', fontWeight: i === breadcrumb.length - 1 ? 400 : 500, '&:hover': i === breadcrumb.length - 1 ? {} : { bgcolor: '#f1f3f4' } }}>
                {n.name}
              </Box>
            ))}
          </Breadcrumbs>
          {breadcrumb.length > 0 && (
            <Tooltip title="Up one level">
              <IconButton size="small" onClick={goUp}><ArrowUpIcon fontSize="small" /></IconButton>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          {/* Search */}
          <TextField
            inputRef={searchRef}
            size="small" placeholder="Search in DriveX                          /"
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width: 420, '& .MuiOutlinedInput-root': { bgcolor: '#f1f3f4', borderRadius: '24px', '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: '1px solid #1a73e8' } } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#5f6368' }} /></InputAdornment>,
              endAdornment: <InputAdornment position="end"><Tooltip title="Filters"><IconButton size="small"><TuneIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip></InputAdornment>,
            }}
          />
          <Tooltip title="Sort">
            <IconButton size="small" onClick={(e) => setSortMenu(e.currentTarget)}><SortIcon /></IconButton>
          </Tooltip>
          <Menu anchorEl={sortMenu} open={!!sortMenu} onClose={() => setSortMenu(null)}>
            {(['updatedAt', 'name', 'fileSize'] as SortKey[]).map(k => (
              <MenuItem key={k} selected={sortKey === k} onClick={() => { setSortKey(k); setSortMenu(null); }}>
                {k === 'updatedAt' ? 'Last modified' : k === 'name' ? 'Name' : 'Size'}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem onClick={() => { setSortAsc(!sortAsc); setSortMenu(null); }}>{sortAsc ? 'Descending' : 'Ascending'}</MenuItem>
          </Menu>
          <Tooltip title="Details">
            <IconButton size="small" onClick={() => setShowInfo(s => !s)} sx={{ color: showInfo ? '#1a73e8' : '#5f6368' }}><InfoIcon /></IconButton>
          </Tooltip>
          <Box sx={{ display: 'flex', bgcolor: '#f1f3f4', borderRadius: '20px', p: '2px' }}>
            <IconButton size="small" onClick={() => setView('grid')} sx={{ borderRadius: '18px', bgcolor: view === 'grid' ? '#fff' : 'transparent', boxShadow: view === 'grid' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
              <GridViewIcon sx={{ fontSize: 18, color: view === 'grid' ? '#1a73e8' : '#5f6368' }} />
            </IconButton>
            <IconButton size="small" onClick={() => setView('list')} sx={{ borderRadius: '18px', bgcolor: view === 'list' ? '#fff' : 'transparent', boxShadow: view === 'list' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
              <ListViewIcon sx={{ fontSize: 18, color: view === 'list' ? '#1a73e8' : '#5f6368' }} />
            </IconButton>
          </Box>
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.25, borderBottom: '1px solid #f1f3f4', flexWrap: 'wrap' }}>
          <FilterChip label="Type" active={category !== 'all'} onClick={(e) => setFilterMenu({ el: e.currentTarget, type: 'type' })} />
          <FilterChip label="People" onClick={(e) => setFilterMenu({ el: e.currentTarget, type: 'people' })} />
          <FilterChip label="Modified" onClick={(e) => setFilterMenu({ el: e.currentTarget, type: 'modified' })} />
          {category !== 'all' && (
            <Chip
              label={`Category: ${category}`}
              onDelete={() => setCategory('all')}
              size="small"
              sx={{ bgcolor: '#d3e3fd', color: '#001d35', fontWeight: 500 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {tab === 'trash' && counts.trash > 0 && (
            <Button size="small" startIcon={<DeleteForeverIcon />} onClick={emptyTrash} sx={{ textTransform: 'none', color: '#d93025' }}>Empty trash</Button>
          )}
          <Button size="small" startIcon={<UploadIcon />} onClick={() => fileInput.current?.click()} sx={{ textTransform: 'none', color: '#3c4043', border: '1px solid #dadce0', borderRadius: '16px', px: 1.5 }}>Upload</Button>
          <Button size="small" startIcon={<NewFolderIcon />} onClick={() => setCreateDlg({ open: true, name: '' })} sx={{ textTransform: 'none', color: '#3c4043', border: '1px solid #dadce0', borderRadius: '16px', px: 1.5 }}>New folder</Button>
        </Box>

        {/* Bulk action bar */}
        {selected.size > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1, bgcolor: '#d3e3fd', borderBottom: '1px solid #c2d3ec' }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: '#1967d2' }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#001d35', mr: 1 }}>{selected.size} selected</Typography>
            {tab !== 'trash' && (
              <>
                <Button size="small" startIcon={<ShareIcon />} onClick={() => { const it = items.find(x => selected.has(x.id)); if (it) setShareDlg({ open: true, item: it }); }} sx={{ textTransform: 'none', color: '#001d35' }}>Share</Button>
                <Button size="small" startIcon={<StarIcon />} onClick={() => bulkStar(true)} sx={{ textTransform: 'none', color: '#001d35' }}>Star</Button>
                <Button size="small" startIcon={<DownloadIcon />} onClick={() => { const it = items.find(x => selected.has(x.id) && x.type === 'file'); if (it) handleDownload(it); }} sx={{ textTransform: 'none', color: '#001d35' }}>Download</Button>
                <Button size="small" startIcon={<DeleteIcon />} onClick={bulkTrash} sx={{ textTransform: 'none', color: '#001d35' }}>Move to trash</Button>
              </>
            )}
            {tab === 'trash' && (
              <Button size="small" startIcon={<RestoreIcon />} onClick={async () => { for (const id of selected) { const it = items.find(x => x.id === id); if (it) await restoreItem(it); } setSelected(new Set()); }} sx={{ textTransform: 'none', color: '#001d35' }}>Restore</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: 'none', color: '#5f6368' }}>Clear</Button>
          </Box>
        )}

        {/* Upload progress */}
        {uploadProgress && (
          <Box sx={{ px: 3, py: 1, bgcolor: '#fff8e1', borderBottom: '1px solid #ffe082' }}>
            <Typography sx={{ fontSize: '0.78rem', mb: 0.5 }}>Uploading {uploadProgress.name} — {uploadProgress.pct}%</Typography>
            <LinearProgress variant="determinate" value={uploadProgress.pct} />
          </Box>
        )}

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: '#1a73e8' }} /></Box>
          ) : filtered.length === 0 ? (
            <EmptyState tab={tab} onUpload={() => fileInput.current?.click()} onNewFolder={() => setCreateDlg({ open: true, name: '' })} />
          ) : (
            <>
              {/* Suggested */}
              {tab === 'files' && !currentFolder && !search && suggested.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <SectionHeader title="Suggested for you" />
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.5 }}>
                    {suggested.map(s => (
                      <SuggestedCard key={s.id} item={s} onOpen={() => openItem(s)} />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Folders */}
              {folders.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <SectionHeader title="Folders" count={folders.length} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
                    {folders.map(f => (
                      <FolderTile
                        key={f.id} item={f}
                        selected={selected.has(f.id)}
                        onOpen={() => openItem(f)}
                        onSelect={(add) => toggleSelect(f.id, add)}
                        onMenu={(el) => setCtx({ el, item: f })}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Files */}
              {files.length > 0 && (
                <Box>
                  <SectionHeader
                    title="Files" count={files.length}
                    right={<Typography sx={{ fontSize: '0.75rem', color: '#5f6368' }}>sorted by {sortKey === 'updatedAt' ? 'Last modified' : sortKey === 'name' ? 'Name' : 'Size'} {sortAsc ? '▲' : '▼'}</Typography>}
                  />
                  {view === 'list' ? (
                    <FilesTable
                      files={files}
                      selected={selected}
                      onToggleSelect={(id, add) => toggleSelect(id, add)}
                      onOpen={openItem}
                      onStar={toggleStar}
                      onMenu={(el, it) => setCtx({ el, item: it })}
                      sortKey={sortKey} sortAsc={sortAsc}
                      onSort={(k) => { if (k === sortKey) setSortAsc(!sortAsc); else { setSortKey(k); setSortAsc(false); } }}
                      myEmail={myEmail}
                    />
                  ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5 }}>
                      {files.map(f => (
                        <FileCard
                          key={f.id} item={f}
                          selected={selected.has(f.id)}
                          onOpen={() => openItem(f)}
                          onSelect={(add) => toggleSelect(f.id, add)}
                          onStar={() => toggleStar(f)}
                          onMenu={(el) => setCtx({ el, item: f })}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Drag-drop overlay */}
        {dragOver && (
          <Box sx={{ position: 'absolute', inset: 8, bgcolor: 'rgba(232,240,254,0.94)', border: '3px dashed #1a73e8', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 50, pointerEvents: 'none' }}>
            <UploadIcon sx={{ fontSize: 64, color: '#1a73e8', mb: 1.5 }} />
            <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, color: '#1967d2' }}>
              Drop files to upload {breadcrumb.length > 0 ? `to "${breadcrumb[breadcrumb.length - 1].name}"` : 'to My DriveX'}
            </Typography>
          </Box>
        )}

        {/* Right details drawer */}
        {showInfo && (
          <Box sx={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 320, bgcolor: '#fff', borderLeft: '1px solid #e8eaed', p: 2, overflow: 'auto', zIndex: 4, boxShadow: '-4px 0 8px rgba(0,0,0,0.04)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>Details</Typography>
              <IconButton size="small" onClick={() => setShowInfo(false)}><CloseIcon fontSize="small" /></IconButton>
            </Box>
            {selected.size === 1 ? (() => {
              const it = items.find(x => selected.has(x.id));
              if (!it) return null;
              return (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, bgcolor: '#f8f9fa', borderRadius: 2, mb: 2 }}>
                    {itemIcon(it, 64)}
                    <Typography sx={{ mt: 1.5, fontWeight: 500 }}>{it.name}</Typography>
                  </Box>
                  <DetailRow label="Type" value={it.type === 'folder' ? 'Folder' : (it.mimeType || 'File')} />
                  <DetailRow label="Size" value={formatBytes(it.fileSize)} />
                  <DetailRow label="Owner" value={it.ownerEmail || (it.ownerId === myId ? 'me' : it.ownerId)} />
                  <DetailRow label="Modified" value={new Date(it.updatedAt).toLocaleString()} />
                  <DetailRow label="Created" value={new Date(it.createdAt).toLocaleString()} />
                  <DetailRow label="Shared" value={it.sharedWith.length === 0 ? 'Private' : `${it.sharedWith.length} people`} />
                  <DetailRow label="Share link" value={it.isShareLinkEnabled ? 'Enabled' : 'Disabled'} />
                </>
              );
            })() : (
              <Typography sx={{ fontSize: '0.85rem', color: '#5f6368' }}>Select a file or folder to see its details, sharing, activity and version history here.</Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Hidden inputs */}
      <input ref={fileInput} type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(e.target.files)} />
      <input ref={folderInput} type="file" multiple hidden onChange={(e) => e.target.files && uploadFiles(e.target.files)} {...({ webkitdirectory: 'true', directory: 'true' } as any)} />

      {/* New menu */}
      <Menu anchorEl={newMenu} open={!!newMenu} onClose={() => setNewMenu(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <MenuItem onClick={() => { setNewMenu(null); setCreateDlg({ open: true, name: '' }); }}>
          <ListItemIcon><NewFolderIcon fontSize="small" /></ListItemIcon><ListItemText>New folder</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { setNewMenu(null); fileInput.current?.click(); }}>
          <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon><ListItemText>File upload</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setNewMenu(null); folderInput.current?.click(); }}>
          <ListItemIcon><FolderIcon fontSize="small" /></ListItemIcon><ListItemText>Folder upload</ListItemText>
        </MenuItem>
      </Menu>

      {/* Filter menus */}
      <Menu anchorEl={filterMenu?.el || null} open={filterMenu?.type === 'type'} onClose={() => setFilterMenu(null)}>
        {(['all', 'image', 'pdf', 'video', 'audio', 'archive', 'doc'] as CategoryFilter[]).map(c => (
          <MenuItem key={c} selected={category === c} onClick={() => { setCategory(c); setFilterMenu(null); }}>
            {c === 'all' ? 'Any type' : c === 'image' ? 'Images' : c === 'pdf' ? 'PDFs' : c === 'video' ? 'Videos' : c === 'audio' ? 'Audio' : c === 'archive' ? 'Archives' : 'Documents'}
          </MenuItem>
        ))}
      </Menu>

      {/* Context menu */}
      <Menu anchorEl={ctx?.el || null} open={!!ctx} onClose={() => setCtx(null)}>
        {ctx && !ctx.item.isDeleted && [
          <MenuItem key="open" onClick={() => { openItem(ctx.item); setCtx(null); }}>
            <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon><ListItemText>Open</ListItemText>
          </MenuItem>,
          ctx.item.type === 'file' && isPreviewable(ctx.item.mimeType) && (
            <MenuItem key="pv" onClick={() => { setPreviewDlg({ open: true, item: ctx.item }); setCtx(null); }}>
              <ListItemIcon><PreviewIcon fontSize="small" /></ListItemIcon><ListItemText>Quick view</ListItemText>
            </MenuItem>
          ),
          <MenuItem key="share" onClick={() => { setShareDlg({ open: true, item: ctx.item }); setCtx(null); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon><ListItemText>Share</ListItemText>
          </MenuItem>,
          <MenuItem key="link" onClick={() => { toggleShareLink(ctx.item); setCtx(null); }}>
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{ctx.item.isShareLinkEnabled ? 'Disable share link' : 'Get share link'}</ListItemText>
          </MenuItem>,
          ctx.item.isShareLinkEnabled && (
            <MenuItem key="copy" onClick={() => { copyShareLink(ctx.item); setCtx(null); }}>
              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon><ListItemText>Copy share link</ListItemText>
            </MenuItem>
          ),
          <MenuItem key="rename" onClick={() => { setRenameDlg({ open: true, item: ctx.item, name: ctx.item.name }); setCtx(null); }}>
            <ListItemIcon><RenameIcon fontSize="small" /></ListItemIcon><ListItemText>Rename</ListItemText>
          </MenuItem>,
          <MenuItem key="dup" onClick={() => { duplicate(ctx.item); setCtx(null); }}>
            <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon><ListItemText>Make a copy</ListItemText>
          </MenuItem>,
          <MenuItem key="star" onClick={() => { toggleStar(ctx.item); setCtx(null); }}>
            <ListItemIcon>{ctx.item.isStarred ? <StarIcon fontSize="small" sx={{ color: '#fbbc04' }} /> : <StarBorderIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{ctx.item.isStarred ? 'Remove from Starred' : 'Add to Starred'}</ListItemText>
          </MenuItem>,
          ctx.item.type === 'file' && (
            <MenuItem key="dl" onClick={() => { handleDownload(ctx.item); setCtx(null); }}>
              <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon><ListItemText>Download</ListItemText>
            </MenuItem>
          ),
          <Divider key="div" />,
          <MenuItem key="trash" onClick={() => { trashItem(ctx.item); setCtx(null); }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#d93025' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#d93025' }}>Move to trash</ListItemText>
          </MenuItem>,
        ]}
        {ctx && ctx.item.isDeleted && [
          <MenuItem key="r" onClick={() => { restoreItem(ctx.item); setCtx(null); }}>
            <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon><ListItemText>Restore</ListItemText>
          </MenuItem>,
          <MenuItem key="pd" onClick={() => { permDelete(ctx.item); setCtx(null); }}>
            <ListItemIcon><DeleteForeverIcon fontSize="small" sx={{ color: '#d93025' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#d93025' }}>Delete forever</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* New folder dialog */}
      <Dialog open={createDlg.open} onClose={() => setCreateDlg({ open: false, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>New folder</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth placeholder="Untitled folder" value={createDlg.name} onChange={e => setCreateDlg({ ...createDlg, name: e.target.value })} onKeyDown={e => e.key === 'Enter' && createFolder()} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDlg({ open: false, name: '' })}>Cancel</Button>
          <Button variant="contained" onClick={createFolder}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={renameDlg.open} onClose={() => setRenameDlg({ open: false, item: null, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Rename</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth value={renameDlg.name} onChange={e => setRenameDlg({ ...renameDlg, name: e.target.value })} onKeyDown={e => e.key === 'Enter' && renameItem()} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDlg({ open: false, item: null, name: '' })}>Cancel</Button>
          <Button variant="contained" onClick={renameItem}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareDlg.open} onClose={() => setShareDlg({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Share "{shareDlg.item?.name}"</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Add people by email" value={shareEmail} onChange={e => setShareEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && addShare()} />
            <TextField select size="small" value={sharePerm} onChange={e => setSharePerm(e.target.value as any)} sx={{ minWidth: 100 }}>
              <MenuItem value="view">Viewer</MenuItem>
              <MenuItem value="edit">Editor</MenuItem>
            </TextField>
            <Button variant="contained" onClick={addShare} disabled={!shareEmail}>Send</Button>
          </Box>
          {shareDlg.item?.sharedWith && shareDlg.item.sharedWith.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>People with access</Typography>
              {shareDlg.item.sharedWith.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                  <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: '#1a73e8', fontSize: '0.75rem' }}>{s.email[0]?.toUpperCase()}</Avatar>
                  <Typography variant="body2" sx={{ flex: 1 }}>{s.email}</Typography>
                  <Chip size="small" label={s.permission} sx={{ mr: 1 }} />
                  <IconButton size="small" onClick={() => removeShare(s.email)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              ))}
            </>
          )}
          {shareDlg.item && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle2">Share link</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {shareDlg.item.isShareLinkEnabled ? 'Anyone with the link can view' : 'Disabled'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {shareDlg.item.isShareLinkEnabled && (
                    <Button size="small" startIcon={<CopyIcon />} onClick={() => copyShareLink(shareDlg.item!)}>Copy link</Button>
                  )}
                  <Button size="small" variant={shareDlg.item.isShareLinkEnabled ? 'outlined' : 'contained'} onClick={() => toggleShareLink(shareDlg.item!)}>
                    {shareDlg.item.isShareLinkEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDlg({ open: false, item: null })}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Quick view dialog */}
      <Dialog open={previewDlg.open} onClose={() => setPreviewDlg({ open: false, item: null })} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '85vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5 }}>
          {previewDlg.item && itemIcon(previewDlg.item, 22)}
          <Box sx={{ flex: 1, fontSize: '1rem', fontWeight: 500 }}>{previewDlg.item?.name}</Box>
          <Tooltip title="Download"><IconButton onClick={() => previewDlg.item && handleDownload(previewDlg.item)}><DownloadIcon /></IconButton></Tooltip>
          <Tooltip title="Share"><IconButton onClick={() => { if (previewDlg.item) { setShareDlg({ open: true, item: previewDlg.item }); setPreviewDlg({ open: false, item: null }); } }}><ShareIcon /></IconButton></Tooltip>
          <IconButton onClick={() => setPreviewDlg({ open: false, item: null })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#202124', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {previewDlg.item && (() => {
            const m = previewDlg.item.mimeType || '';
            const src = `/api/drivex/${previewDlg.item.id}/download`;
            if (m.startsWith('image/')) return <img src={src} alt={previewDlg.item.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />;
            if (m === 'application/pdf') return <iframe src={src} style={{ width: '100%', height: '100%', border: 0 }} title="preview" />;
            if (m.startsWith('video/')) return <video src={src} controls style={{ maxWidth: '100%', maxHeight: '100%' }} />;
            if (m.startsWith('audio/')) return <audio src={src} controls />;
            if (m.startsWith('text/')) return <iframe src={src} style={{ width: '100%', height: '100%', background: '#fff' }} title="preview" />;
            return <Typography sx={{ color: '#fff' }}>No preview available</Typography>;
          })()}
        </DialogContent>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert onClose={() => setSnack(null)} severity={snack.sev} sx={{ width: '100%' }}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

// ─────────────────── Subcomponents ───────────────────

const RailItem: React.FC<{ icon: React.ReactNode; label: string; active?: boolean; onClick?: () => void; count?: number }> = ({ icon, label, active, onClick, count }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', gap: 2, height: 36, pl: 3, pr: 2, mr: 1,
      borderRadius: '0 18px 18px 0', cursor: 'pointer',
      bgcolor: active ? '#e8f0fe' : 'transparent',
      color: active ? '#1967d2' : '#202124',
      fontWeight: active ? 600 : 400, fontSize: '0.87rem',
      '&:hover': { bgcolor: active ? '#e8f0fe' : '#f1f3f4' },
      '& svg': { fontSize: 20, color: active ? '#1967d2' : 'inherit' },
    }}
  >
    {icon}
    <Box sx={{ flex: 1 }}>{label}</Box>
    {count !== undefined && <Typography sx={{ fontSize: '0.72rem', color: '#80868b' }}>{count}</Typography>}
  </Box>
);

const FilterChip: React.FC<{ label: string; active?: boolean; onClick: (e: React.MouseEvent<HTMLElement>) => void }> = ({ label, active, onClick }) => (
  <Chip
    label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{label}<ArrowDropDownIcon sx={{ fontSize: 16 }} /></Box>}
    size="small" onClick={onClick}
    sx={{
      borderRadius: '16px', height: 32, px: 1, border: '1px solid #dadce0',
      bgcolor: active ? '#d3e3fd' : '#fff', color: active ? '#001d35' : '#3c4043',
      fontWeight: active ? 500 : 400,
      '&:hover': { bgcolor: active ? '#d3e3fd' : '#f8f9fa' },
    }}
  />
);

const SectionHeader: React.FC<{ title: string; count?: number; right?: React.ReactNode }> = ({ title, count, right }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#5f6368' }}>
      {title}{count !== undefined && ` · ${count}`}
    </Typography>
    <Box sx={{ flex: 1 }} />
    {right}
  </Box>
);

const SuggestedCard: React.FC<{ item: DriveItem; onOpen: () => void }> = ({ item, onOpen }) => (
  <Paper elevation={0} onClick={onOpen} sx={{ border: '1px solid #e8eaed', borderRadius: 2, overflow: 'hidden', cursor: 'pointer', '&:hover': { borderColor: '#1a73e8', boxShadow: '0 1px 6px rgba(26,115,232,0.18)' } }}>
    <Box sx={{ height: 110, background: 'linear-gradient(135deg,#e8f0fe 0%, #f8faff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {itemIcon(item, 44)}
    </Box>
    <Box sx={{ px: 1.25, py: 1 }}>
      <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{item.name}</Typography>
      <Typography sx={{ fontSize: '0.72rem', color: '#5f6368', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
        <ClockIcon sx={{ fontSize: 12 }} /> {formatRel(item.updatedAt)}
      </Typography>
    </Box>
  </Paper>
);

const FolderTile: React.FC<{ item: DriveItem; selected: boolean; onOpen: () => void; onSelect: (additive: boolean) => void; onMenu: (el: HTMLElement) => void }> = ({ item, selected, onOpen, onSelect, onMenu }) => (
  <Box
    onClick={(e) => { if (e.detail === 2) onOpen(); else onSelect(e.ctrlKey || e.metaKey); }}
    onDoubleClick={onOpen}
    sx={{
      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, borderRadius: 2, cursor: 'pointer',
      bgcolor: selected ? '#d3e3fd' : '#f8f9fa',
      border: selected ? '1px solid #a8c7fa' : '1px solid transparent',
      '&:hover': { bgcolor: selected ? '#d3e3fd' : '#f1f3f4', '& .row-more': { opacity: 1 } },
    }}
  >
    {itemIcon(item, 22)}
    <Typography noWrap sx={{ flex: 1, fontSize: '0.87rem', fontWeight: 500 }}>{item.name}</Typography>
    {item.isStarred && <StarIcon sx={{ fontSize: 16, color: '#fbbc04' }} />}
    <IconButton size="small" className="row-more" onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget); }} sx={{ opacity: 0, p: 0.25 }}>
      <MoreVertIcon fontSize="small" />
    </IconButton>
  </Box>
);

const FileCard: React.FC<{ item: DriveItem; selected: boolean; onOpen: () => void; onSelect: (additive: boolean) => void; onStar: () => void; onMenu: (el: HTMLElement) => void }> = ({ item, selected, onOpen, onSelect, onStar, onMenu }) => (
  <Paper elevation={0} sx={{
    position: 'relative', border: selected ? '1px solid #1a73e8' : '1px solid #e8eaed', borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
    bgcolor: selected ? '#e8f0fe' : '#fff',
    '&:hover': { borderColor: '#1a73e8', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', '& .ck, & .row-more': { opacity: 1 } },
  }} onClick={(e) => onSelect(e.ctrlKey || e.metaKey)} onDoubleClick={onOpen}>
    <Checkbox className="ck" size="small" checked={selected} onClick={(e) => { e.stopPropagation(); onSelect(true); }} sx={{ position: 'absolute', top: 6, left: 6, zIndex: 2, p: 0.25, bgcolor: '#fff', borderRadius: '4px', opacity: selected ? 1 : 0 }} />
    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStar(); }} sx={{ position: 'absolute', top: 6, right: 36, zIndex: 2, p: 0.25, color: item.isStarred ? '#fbbc04' : '#dadce0' }}>
      {item.isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
    </IconButton>
    <IconButton size="small" className="row-more" onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget); }} sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, p: 0.25, opacity: 0 }}>
      <MoreVertIcon fontSize="small" />
    </IconButton>
    <Box sx={{ height: 130, bgcolor: '#f1f3f4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {itemIcon(item, 44)}
    </Box>
    <Box sx={{ p: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {itemIcon(item, 14)}
        <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 500, flex: 1 }}>{item.name}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.7rem', color: '#5f6368', mt: 0.25 }}>{formatBytes(item.fileSize)} · {formatRel(item.updatedAt)}</Typography>
    </Box>
  </Paper>
);

const FilesTable: React.FC<{
  files: DriveItem[]; selected: Set<string>;
  onToggleSelect: (id: string, additive: boolean) => void;
  onOpen: (it: DriveItem) => void; onStar: (it: DriveItem) => void;
  onMenu: (el: HTMLElement, it: DriveItem) => void;
  sortKey: SortKey; sortAsc: boolean; onSort: (k: SortKey) => void;
  myEmail?: string;
}> = ({ files, selected, onToggleSelect, onOpen, onStar, onMenu, sortKey, sortAsc, onSort, myEmail }) => {
  const Th: React.FC<{ k?: SortKey; w?: any; right?: boolean; children: React.ReactNode }> = ({ k, w, right, children }) => (
    <Box component="th" onClick={k ? () => onSort(k) : undefined} sx={{
      width: w, textAlign: right ? 'right' : 'left', p: '10px 12px', fontSize: '0.74rem',
      color: '#5f6368', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
      borderBottom: '1px solid #e8eaed', cursor: k ? 'pointer' : 'default', userSelect: 'none',
      '&:hover': k ? { color: '#202124' } : {},
    }}>
      {children}
      {k && sortKey === k && <Box component="span" sx={{ ml: 0.5 }}>{sortAsc ? '▲' : '▼'}</Box>}
    </Box>
  );
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
      <Box component="thead">
        <Box component="tr">
          <Th w={28}>{null}</Th>
          <Th k="name">Name</Th>
          <Th w={140}>Owner</Th>
          <Th k="updatedAt" w={170}>Last modified</Th>
          <Th k="fileSize" w={100} right>File size</Th>
          <Th w={32}>{null}</Th>
        </Box>
      </Box>
      <Box component="tbody">
        {files.map(it => {
          const isSel = selected.has(it.id);
          return (
            <Box component="tr" key={it.id}
              onClick={(e: React.MouseEvent) => onToggleSelect(it.id, e.ctrlKey || e.metaKey)}
              onDoubleClick={() => onOpen(it)}
              sx={{ cursor: 'pointer', bgcolor: isSel ? '#d3e3fd' : 'transparent', '&:hover': { bgcolor: isSel ? '#d3e3fd' : '#f8f9fa', '& .star, & .more': { opacity: 1 } } }}>
              <Box component="td" sx={{ p: '6px 8px', borderBottom: '1px solid #f1f3f4', textAlign: 'center' }}>
                <IconButton size="small" className="star" onClick={(e) => { e.stopPropagation(); onStar(it); }} sx={{ p: 0.25, color: it.isStarred ? '#fbbc04' : '#dadce0', opacity: it.isStarred ? 1 : 0 }}>
                  {it.isStarred ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
              <Box component="td" sx={{ p: '6px 12px', borderBottom: '1px solid #f1f3f4' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {itemIcon(it, 22)}
                  <Typography noWrap sx={{ fontSize: '0.87rem', color: '#202124' }}>{it.name}</Typography>
                  {it.sharedWith && it.sharedWith.length > 0 && <Chip size="small" label="Shared" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e6f4ea', color: '#137333', '& .MuiChip-label': { px: 0.75 } }} />}
                  {it.isShareLinkEnabled && <Chip size="small" label="Link" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e8f0fe', color: '#1967d2', '& .MuiChip-label': { px: 0.75 } }} />}
                </Box>
              </Box>
              <Box component="td" sx={{ p: '6px 12px', borderBottom: '1px solid #f1f3f4', color: '#3c4043', fontSize: '0.8rem' }}>
                {it.ownerEmail === myEmail || !it.ownerEmail ? 'me' : it.ownerEmail}
              </Box>
              <Box component="td" sx={{ p: '6px 12px', borderBottom: '1px solid #f1f3f4', color: '#3c4043', fontSize: '0.8rem' }}>{formatRel(it.updatedAt)}</Box>
              <Box component="td" sx={{ p: '6px 12px', borderBottom: '1px solid #f1f3f4', color: '#3c4043', fontSize: '0.8rem', textAlign: 'right' }}>{formatBytes(it.fileSize)}</Box>
              <Box component="td" sx={{ p: '6px 8px', borderBottom: '1px solid #f1f3f4', textAlign: 'right' }}>
                <IconButton size="small" className="more" onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget, it); }} sx={{ p: 0.25, opacity: 0 }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const EmptyState: React.FC<{ tab: ViewTab; onUpload: () => void; onNewFolder: () => void }> = ({ tab, onUpload, onNewFolder }) => {
  const map: Record<ViewTab, { title: string; sub: string; icon: React.ReactNode }> = {
    files:   { title: 'This folder is empty',           sub: 'Drag files here or use Upload', icon: <FolderIcon sx={{ fontSize: 56, color: '#1a73e8' }} /> },
    recent:  { title: 'Nothing recent',                  sub: 'Files you open will show up here.', icon: <ClockIcon sx={{ fontSize: 56, color: '#1a73e8' }} /> },
    starred: { title: 'No starred items',                sub: 'Star any file to keep it close.', icon: <StarBorderIcon sx={{ fontSize: 56, color: '#fbbc04' }} /> },
    shared:  { title: 'Nothing shared with you yet',     sub: 'When teammates share files, they appear here.', icon: <PeopleIcon sx={{ fontSize: 56, color: '#0f9d58' }} /> },
    trash:   { title: 'Trash is empty',                  sub: 'Items you delete stay here for 30 days.', icon: <DeleteIcon sx={{ fontSize: 56, color: '#5f6368' }} /> },
  };
  const e = map[tab];
  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Box sx={{ width: 100, height: 100, mx: 'auto', mb: 2, borderRadius: '50%', bgcolor: '#f1f3f4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{e.icon}</Box>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{e.title}</Typography>
      <Typography sx={{ fontSize: '0.9rem', color: '#5f6368', mt: 0.5 }}>{e.sub}</Typography>
      {tab === 'files' && (
        <Box sx={{ mt: 2.5, display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={onUpload} sx={{ borderRadius: '24px', textTransform: 'none' }}>Upload</Button>
          <Button variant="outlined" startIcon={<NewFolderIcon />} onClick={onNewFolder} sx={{ borderRadius: '24px', textTransform: 'none' }}>New folder</Button>
        </Box>
      )}
    </Box>
  );
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', py: 0.75, borderBottom: '1px solid #f1f3f4' }}>
    <Typography sx={{ fontSize: '0.78rem', color: '#5f6368', width: 100, flexShrink: 0 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.82rem', color: '#202124', flex: 1, wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);
